var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useState, useCallback, useRef } from "react";
import { nodesApi } from "@api/enpoints/nodes-api";
const DEBOUNCE_DELAY = 300;
export const useNodesFilterDropdowns = () => {
    const [options, setOptions] = useState({
        nodeUids: [],
        nodeNames: [],
        apUids: [],
        apNames: [],
    });
    const [loading, setLoading] = useState({
        nodeUids: false,
        nodeNames: false,
        apUids: false,
        apNames: false,
    });
    // Debounce timers refs
    const debounceTimers = useRef({
        nodeUids: null,
        nodeNames: null,
        apUids: null,
        apNames: null,
    });
    // Search for Node UIDs
    const searchNodeUids = useCallback((searchTerm) => {
        // Clear previous timer
        if (debounceTimers.current.nodeUids) {
            clearTimeout(debounceTimers.current.nodeUids);
        }
        setLoading((prev) => (Object.assign(Object.assign({}, prev), { nodeUids: true })));
        debounceTimers.current.nodeUids = setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                // If empty, fetch all defaults; otherwise search
                const response = yield nodesApi.getDropdowns(searchTerm ? { nodeUidSearch: searchTerm } : {});
                if (response.data.isSuccess && response.data.data) {
                    setOptions((prev) => (Object.assign(Object.assign({}, prev), { nodeUids: response.data.data.nodeUids })));
                }
            }
            catch (error) {
                console.error("Failed to fetch node UIDs:", error);
            }
            finally {
                setLoading((prev) => (Object.assign(Object.assign({}, prev), { nodeUids: false })));
            }
        }), DEBOUNCE_DELAY);
    }, []);
    // Search for Node Names
    const searchNodeNames = useCallback((searchTerm) => {
        // Clear previous timer
        if (debounceTimers.current.nodeNames) {
            clearTimeout(debounceTimers.current.nodeNames);
        }
        setLoading((prev) => (Object.assign(Object.assign({}, prev), { nodeNames: true })));
        debounceTimers.current.nodeNames = setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                // If empty, fetch all defaults; otherwise search
                const response = yield nodesApi.getDropdowns(searchTerm ? { nodeNameSearch: searchTerm } : {});
                if (response.data.isSuccess && response.data.data) {
                    setOptions((prev) => (Object.assign(Object.assign({}, prev), { nodeNames: response.data.data.nodeNames })));
                }
            }
            catch (error) {
                console.error("Failed to fetch node names:", error);
            }
            finally {
                setLoading((prev) => (Object.assign(Object.assign({}, prev), { nodeNames: false })));
            }
        }), DEBOUNCE_DELAY);
    }, []);
    // Search for AP UIDs
    const searchApUids = useCallback((searchTerm) => {
        // Clear previous timer
        if (debounceTimers.current.apUids) {
            clearTimeout(debounceTimers.current.apUids);
        }
        setLoading((prev) => (Object.assign(Object.assign({}, prev), { apUids: true })));
        debounceTimers.current.apUids = setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                // If empty, fetch all defaults; otherwise search
                const response = yield nodesApi.getDropdowns(searchTerm ? { apUidSearch: searchTerm } : {});
                if (response.data.isSuccess && response.data.data) {
                    setOptions((prev) => (Object.assign(Object.assign({}, prev), { apUids: response.data.data.apUids })));
                }
            }
            catch (error) {
                console.error("Failed to fetch AP UIDs:", error);
            }
            finally {
                setLoading((prev) => (Object.assign(Object.assign({}, prev), { apUids: false })));
            }
        }), DEBOUNCE_DELAY);
    }, []);
    // Search for AP Names
    const searchApNames = useCallback((searchTerm) => {
        // Clear previous timer
        if (debounceTimers.current.apNames) {
            clearTimeout(debounceTimers.current.apNames);
        }
        setLoading((prev) => (Object.assign(Object.assign({}, prev), { apNames: true })));
        debounceTimers.current.apNames = setTimeout(() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                // If empty, fetch all defaults; otherwise search
                const response = yield nodesApi.getDropdowns(searchTerm ? { apNameSearch: searchTerm } : {});
                if (response.data.isSuccess && response.data.data) {
                    setOptions((prev) => (Object.assign(Object.assign({}, prev), { apNames: response.data.data.apNames })));
                }
            }
            catch (error) {
                console.error("Failed to fetch AP names:", error);
            }
            finally {
                setLoading((prev) => (Object.assign(Object.assign({}, prev), { apNames: false })));
            }
        }), DEBOUNCE_DELAY);
    }, []);
    // Load initial options when filter dialog opens (fetches all without search term)
    const loadInitialOptions = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setLoading({
            nodeUids: true,
            nodeNames: true,
            apUids: true,
            apNames: true,
        });
        try {
            // Fetch all dropdowns with empty search (returns all options)
            const response = yield nodesApi.getDropdowns({});
            if (response.data.isSuccess && response.data.data) {
                setOptions({
                    nodeUids: response.data.data.nodeUids || [],
                    nodeNames: response.data.data.nodeNames || [],
                    apUids: response.data.data.apUids || [],
                    apNames: response.data.data.apNames || [],
                });
            }
        }
        catch (error) {
            console.error("Failed to load initial dropdown options:", error);
        }
        finally {
            setLoading({
                nodeUids: false,
                nodeNames: false,
                apUids: false,
                apNames: false,
            });
        }
    }), []);
    // Clear all options
    const clearOptions = useCallback(() => {
        setOptions({
            nodeUids: [],
            nodeNames: [],
            apUids: [],
            apNames: [],
        });
    }, []);
    return {
        options,
        loading,
        searchNodeUids,
        searchNodeNames,
        searchApUids,
        searchApNames,
        clearOptions,
        loadInitialOptions,
    };
};
